/**
 * 路由 → moduleId 解析。
 *
 * 规则：moduleId 取浏览器完整 pathname（含 qiankun 子应用 base），与 window.location.pathname 一致。
 * 各应用的映射表 / 显隐规则由 `setAiRuntime()` 注入，这里只做通用匹配。
 */
import { getAiRuntime, type ModuleMeta } from '../runtime';

export type { ModuleMeta };

const matchPrefix = (pathname: string, k: string) => pathname === k || pathname.startsWith(k + '/');

/**
 * 归一化为含 appBase 的完整 pathname。
 *
 * `subscribeRoute` 的契约是回调带 appBase，但 umi3（history v4）配了 `base` 时
 * `createBrowserHistory({ basename })` 会把 basename 从 `location.pathname` 里剥掉，
 * 宿主若直接 `cb(location.pathname)` 就会与首屏的 `window.location.pathname` 不一致 ——
 * 站内跳转后匹配不到任何 key，moduleId 变空、黑名单也失效。
 * umi4（history v5 无 basename）不剥，此处是 no-op。
 */
const withAppBase = (pathname: string): string => {
  const base = (getAiRuntime().appBase || '').replace(/\/+$/, '');
  if (!base || matchPrefix(pathname, base)) return pathname;
  return base + (pathname.startsWith('/') ? pathname : `/${pathname}`);
};

/** 最长前缀匹配 */
export function resolveModule(pathname: string): ModuleMeta | undefined {
  const map = getAiRuntime().moduleMap;
  const path = withAppBase(pathname);
  const matched = Object.keys(map)
    .filter((k) => matchPrefix(path, k))
    .sort((a, b) => b.length - a.length)[0];
  return matched ? map[matched] : undefined;
}

/**
 * 浮窗是否隐藏。
 * - 配了 `showPaths`（白名单）→ 不在白名单即隐藏
 * - 否则按 `hidePrefixes`（黑名单）
 */
export function shouldHideFloating(pathname: string): boolean {
  const { showPaths, hidePrefixes = [] } = getAiRuntime();
  const path = withAppBase(pathname);
  if (showPaths?.length) {
    return !showPaths.some((k) => matchPrefix(path, k));
  }
  return hidePrefixes.some((k) => matchPrefix(path, k));
}
